import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  DialogConfirmComponent,
  DialogConfirmData,
  DialogLoadingComponent,
  DialogMessageComponent,
  DialogMessageData,
} from '@ui';
import { catchError, finalize, Observable, throwError, timeout } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  constructor(private dialog: MatDialog) {}

  loading(
    disableCloseDialog: boolean,
    timeout?: number,
  ): MatDialogRef<DialogLoadingComponent, any> {
    const dialogRef = this.dialog.open(DialogLoadingComponent, {
      panelClass: 'loading-dialog',
      autoFocus: false,
      disableClose: disableCloseDialog,
    });

    if (timeout) {
      setTimeout(() => {
        dialogRef.close();
      }, timeout);
    }

    return dialogRef;
  }

  closeLoading(
    dialog: MatDialogRef<DialogLoadingComponent, any>,
    timeout: number,
  ) {
    setTimeout(() => {
      dialog.close();
    }, timeout);
  }

  confirm(data: DialogConfirmData) {
    return this.dialog.open(DialogConfirmComponent, { data }).afterClosed();
  }

  message(data: DialogMessageData) {
    return this.dialog.open(DialogMessageComponent, {
      autoFocus: false,
      disableClose: true,
      panelClass: 'dialog-message',
      data,
    });
  }

  success(title: string, message?: string) {
    return this.message({ title, message, type: 'success' });
  }
  error(title: string, message: string = 'กรุณาลองใหม่อีกครั้ง') {
    return this.message({ title, message, type: 'error' });
  }
  warning(title: string, message?: string) {
    return this.message({ title, message, type: 'warning' });
  }
  info(title: string, message?: string) {
    return this.message({ title, message, type: 'info' });
  }

  withLoading<T>(
    source$: Observable<T>,
    options?: {
      disableClose?: boolean;
      errorToast?: boolean;
      delay?: number;
      timeoutMs?: number | null;
    },
  ): Observable<T> {
    let ref: MatDialogRef<DialogLoadingComponent>;

    const delay = options?.delay ?? 300;
    const timeoutMs =
      options?.timeoutMs === undefined ? 15000 : options.timeoutMs;

    const timer = setTimeout(() => {
      ref = this.loading(options?.disableClose ?? true);
    }, delay);

    if (timeoutMs === null) {
      return source$.pipe(
        catchError((err) => throwError(() => err)),
        finalize(() => {
          clearTimeout(timer);
          ref?.close();
        }),
      );
    }

    return source$.pipe(
      timeout(timeoutMs),
      catchError((err) => throwError(() => err)),
      finalize(() => {
        clearTimeout(timer);
        ref?.close();
      }),
    );
  }

  runWithLoading(
    action: () => void,
    options?: {
      disableClose?: boolean;
      delay?: number;
    },
  ) {
    const ref = this.loading(options?.disableClose ?? true);

    const delay = options?.delay ?? 300;

    setTimeout(() => {
      try {
        action();
      } finally {
        ref.close();
      }
    }, delay);
  }

  async runAsyncWithLoading<T>(
    action: () => Promise<T>,
    options?: {
      disableClose?: boolean;
      delay?: number;
    },
  ): Promise<T> {
    const delay = options?.delay ?? 300;

    let ref: MatDialogRef<DialogLoadingComponent> | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;

    try {
      timer = setTimeout(() => {
        ref = this.loading(options?.disableClose ?? true);
      }, delay);

      return await action();
    } finally {
      if (timer) {
        clearTimeout(timer);
      }

      ref?.close();
    }
  }
}
